import { createContext, useContext, useEffect, useState } from 'react';

import {
    encodeWithDigit,
    getRandomTwoDigitNumber,
    isEmptyData,
    printChatDebugLog
} from '../utils/SnpChatUtils';
import {
    CHAT_BASE_URL,
    CHAT_SOCKET_ADMIN_URL,
    CHAT_SOCKET_URL,
    ChatStatus,
    HTTP_UNAUTHORIZED,
    MessageType,
    PayLoadType,
    UNFORTUNATE_ERROR
} from '../utils/SnpChatConstant';
import snpWebSocketManager from './SnpWebSocketEngine';
import { chatAxios_get, chatAxios_post } from './SnpChatNetworkUtils';
import { useSnpChatPublicApi } from '../public-snp-sdk';


export const SnpApiContext = createContext();
export const SnpApiProvider = ({ children }) => {


    const [keyShopHash, registerChatKey] = useState(() => {
        return localStorage.getItem('snpChatClientHash') || '';
    });

    const [customToastFunc, setCustomToastFunc] = useState(null);

    const [keyUserDetails, registerUserDetails] = useState(() => {
        const saved = localStorage.getItem('snpChatUserHash');
        return saved ? JSON.parse(saved) : { id: '', name: '', email: '' };
    });

    useEffect(() => {
        printChatDebugLog("ChatContext:keyShopHash=> ", keyShopHash);
        if (keyShopHash) {
            localStorage.setItem('snpChatClientHash', keyShopHash);
        } else {
            localStorage.setItem('snpChatClientHash', "");
        }

    }, [keyShopHash]);

    useEffect(() => {
        printChatDebugLog("ChatContext:keyUserDetails=> ", keyUserDetails);
        if (keyUserDetails) {
            const isValid = keyUserDetails.id || keyUserDetails.name || keyUserDetails.email;
            if (isValid) {
                localStorage.setItem('snpChatUserHash', JSON.stringify(keyUserDetails));
            }
        } else {
            localStorage.setItem('snpChatUserHash', "");
        }
    }, [keyUserDetails]);

    const deRegisterKey = () => {
        printChatDebugLog("ChatContext:deRegisterKey");
        localStorage.removeItem('snpChatClientHash');
        localStorage.removeItem('snpChatUserHash');
        stopWsSession();
        registerChatKey('');
        registerUserDetails({ id: '', name: '', email: '' });
        setCustomToastFunc(null);

    };


    useEffect(() => {
        useSnpChatPublicApi.setCustomToastFunc = setCustomToastFunc;
        useSnpChatPublicApi.registerChatKey = registerChatKey;
        useSnpChatPublicApi.registerUserDetails = registerUserDetails;
        useSnpChatPublicApi.deRegisterKey = deRegisterKey;
        printChatDebugLog("ChatContext: Context Init ... ");
    }, []);



    //Msg format {status : <status> , errorMessage: <errorMessage> , payLoad: <key,value pair> }
    const connectWsSession = (
        clientId,
        admin,
        handleCustomToastFunc = (tag, msg, isError, toneType) => { },
        onSocketOpenClose = (isOpen) => { },
        onTextMessage = (payLoad, snpModel = true) => { }) => {

        if (!snpWebSocketManager.isConnected()) {
            printChatDebugLog("ChatContext:connectWsSession clientId", clientId);
            const parentId = encodeWithDigit(keyShopHash, getRandomTwoDigitNumber());
            clientId = encodeWithDigit(clientId, getRandomTwoDigitNumber());
            printChatDebugLog("ChatContext:connectWsSession parsed ", parentId, clientId);
            if (admin) {
                snpWebSocketManager.connect(parentId, clientId, CHAT_BASE_URL, PayLoadType.AS_CHAT,
                    CHAT_SOCKET_ADMIN_URL, handleCustomToastFunc,
                    onSocketOpenClose);
            } else {
                snpWebSocketManager.connect(parentId, clientId, CHAT_BASE_URL, PayLoadType.AS_CHAT,
                    CHAT_SOCKET_URL, handleCustomToastFunc,
                    onSocketOpenClose);
            }


            snpWebSocketManager.onopen = () => {
                printChatDebugLog("connectWsSession", 'Connected');
                onSocketOpenClose(true);
            };

            snpWebSocketManager.registerCallback((msg) => {
                printChatDebugLog('ChatContext:snpWebSocketManager:Received:', msg);
                //I know this message is object
                try {
                    const payLoad = JSON.parse(msg);
                    onTextMessage(payLoad, true);
                } catch (err) {
                    console.error("ChatContext:snpWebSocketManager:Received as string", err);
                    onTextMessage(msg, false);
                }
            });

            snpWebSocketManager.onopen = () => {
                printChatDebugLog("ChatContext:connectWsSession", 'Connected');
                onSocketOpenClose(true);
            };
        } else {
            printChatDebugLog("ChatContext:connectWsSession session is already there for ");
        }
    };

    const stopWsSession = () => {
        printChatDebugLog("ChatContext:stopWsSession", "init");
        snpWebSocketManager.close();
    }

    const sendMessage = (msg) => {
        snpWebSocketManager.send(msg);
    }

    const sendPayLoadMessage = (msg, payLoadType) => {
        printChatDebugLog("ChatContext:sendPayLoadMessage init", msg, payLoadType);
        const payLoad = {
            status: 0,
            payLoad: { ...msg, payloadType: payLoadType },
        }
        const message = JSON.stringify(payLoad);
        sendMessage(message);
    }

    const createChatSession = async ({ sourceId = "",
        sourceName = "",
        sourceEmail = "",
        message = "",
        messageType = MessageType.TYPE_TEXT
    }) => {
        try {

            if (isEmptyData(keyShopHash)) {
                return { status: HTTP_UNAUTHORIZED, errorMessage: "Chat is not registered(Empty key)" }
            }

            const data = {
                payLoad: {
                    parentId: encodeWithDigit(keyShopHash, getRandomTwoDigitNumber()),
                    sourceId: sourceId,
                    sourceName: sourceName,
                    sourceEmail: sourceEmail,
                    message: message,
                    messageType: messageType,
                    as: PayLoadType.AS_CHAT,
                },
            };
            const payLoad = await chatAxios_post(`create-chat-session`, data);
            if (payLoad !== null) {
                return payLoad;
            } else {
                console.error("ChatContext:createChatSession", "null payload");
            }
        } catch (err) {
            console.error("ChatContext:createChatSession", err);
        }
        return null;
    };

    const uploadImage = (file, sessionId, via, mapId, onprogress = (percent) => { }, onResponse = (response) => { }) => {
        sessionId = encodeWithDigit(sessionId, getRandomTwoDigitNumber());
        const xhr = new XMLHttpRequest();
        xhr.open("POST", `${CHAT_BASE_URL}snp-image-uploader`);

        xhr.upload.onprogress = (e) => {
            if (e.lengthComputable) {
                const percent = (e.loaded / e.total) * 100;
                //console.log(`Progress: ${percent.toFixed(2)}%`);
                onprogress(percent);
            }
        };

        xhr.onload = () => {
            try {
                printChatDebugLog("ChatContext:uploadImage:Status:", xhr.status);
                printChatDebugLog("ChatContext:uploadImage:responseText:", xhr.responseText);

                if (xhr.status >= 200 && xhr.status < 300) {
                    const res = JSON.parse(xhr.responseText);
                    printChatDebugLog("ChatContext:uploadImage:Parsed Response:", res);
                    onResponse(res);
                } else {
                    console.error("ChatContext:uploadImage:Server error:", xhr.responseText || "Unknown error");
                    onResponse({ status: UNFORTUNATE_ERROR, errorMessage: `Network Error [Error Code : ${xhr.status} ${xhr.responseText}]` });
                }
            } catch (err) {
                console.error("ChatContext:uploadImage:Failed to parse JSON:", err);
                onResponse({ status: UNFORTUNATE_ERROR, errorMessage: `Network Error [Error Code : ${xhr.status} ${xhr.responseText}]` });
            }
        };

        xhr.onerror = () => {
            console.error("ChatContext:Network error during upload");
            onResponse({ status: UNFORTUNATE_ERROR, errorMessage: `Network Error [Error Code : ${xhr.status} ${xhr.responseText}]` });
        };

        const form = new FormData();
        form.append("imageFile", file);
        form.append("sessionId", sessionId);
        form.append("via", via);
        form.append("mapId", mapId);

        xhr.send(form);
    };

    const stopChatSession = async (mAdmin, sessionId) => {
        try {
            const data = {
                payLoad: {
                    sessionId: encodeWithDigit(sessionId, getRandomTwoDigitNumber()),
                    admin: mAdmin,
                },
            };
            const payLoad = await chatAxios_post("stop-chat-session", data);
            if (payLoad !== null) {
                return payLoad;
            } else {
                console.error("ChatContext:stopChatSession", "null payload");
            }
        } catch (err) {
            console.error("ChatContext:stopChatSession", err);
        }
        return null;
    };


    const updateCustomerDetails = async (name, email, sessionId) => {
        try {
            const data = {
                payLoad: {
                    sessionId: encodeWithDigit(sessionId, getRandomTwoDigitNumber()),
                    customerName: name,
                    customerEmail: email,
                },
            };
            const payLoad = await chatAxios_post("update-customer-details", data);
            if (payLoad !== null) {
                return payLoad;
            } else {
                console.error("ChatContext:updateCustomerDetails", "null payload");
            }
        } catch (err) {
            console.error("ChatContext:updateCustomerDetails", err);
        }
        return null;
    }

    const getShopMessage = async (chatStatus) => {
        try {
            if (isEmptyData(keyShopHash)) {
                return { status: HTTP_UNAUTHORIZED, errorMessage: "Chat is not registered(Empty key)" }
            }
            if (isEmptyData(keyUserDetails.id)) {
                return { status: HTTP_UNAUTHORIZED, errorMessage: "No User Registered(Empty key)" }
            }

            const data = {
                payLoad: {
                    parentId: encodeWithDigit(keyShopHash, getRandomTwoDigitNumber()),
                    clientId: (chatStatus === ChatStatus.RUNNING_CHAT || chatStatus === ChatStatus.STOP_BY_CUSTOMER)
                        ? encodeWithDigit(keyUserDetails.id, getRandomTwoDigitNumber())
                        : "",
                    chatStatus: chatStatus,
                },
            };
            const payLoad = await chatAxios_post(`get-shop-message`, data);
            return payLoad;
        } catch (err) {
            console.error(`ChatContext:getShopMessage (${chatStatus})`, err);
        }
        return null;
    };

    const updateChatSessionDestinationDetails = async (sessionId) => {
        try {
            if (isEmptyData(keyUserDetails.id)) {
                return { status: HTTP_UNAUTHORIZED, errorMessage: "No User Registered(Empty key)" }
            }
            const data = {
                payLoad: {
                    sessionId: sessionId,
                    destinationId: keyUserDetails.id,
                    destinationName: keyUserDetails.name,
                },
            };
            const payLoad = await chatAxios_post(`update-admin-details`, data);
            return payLoad;
        } catch (err) {
            console.error(`ChatContext:updateChatSessionDestinationDetails`, err);
        }
        return null;
    }

    const getLiveMessages = async (sessionId, msgFromId, page = 1, limit = 10) => {
        try {
            const data = {
                payLoad: {
                    sessionId: encodeWithDigit(sessionId, getRandomTwoDigitNumber()),
                    msgFromId: encodeWithDigit(msgFromId, getRandomTwoDigitNumber()),
                    page: page,
                    limit: limit,
                },
            };
            const payLoad = await chatAxios_post(`get-live-message`, data);
            return payLoad;
        } catch (err) {
            console.error(`ChatContext:getLiveMessages`, err);
        }
        return null;
    };

    const exportChatSession = async (sessionId) => {
        try {
            const data = {
                payLoad: {
                    fileName: `${sessionId}.pdf`,
                    sessionId: sessionId,
                },
            };
            const payLoad = await chatAxios_post(`export-chat-session`, data);
            return payLoad;
        } catch (err) {
            console.error(`ChatContext:exportChatSession`, err);
        }
        return null;
    };

    const getCustomeDetailsAvailable = async (sessionId) => {
        try {
            const payLoad = await chatAxios_get(`get-chat-customer-details?sessionId=${sessionId}`);
            return payLoad;
        } catch (err) {
            console.error(`ChatContext:getCustomeDetailsAvailable`, err);
        }
        return null;
    };

    const requestCustomerUpdate = async (sessionId) => {
        try {
            const payLoad = await chatAxios_get(`request-update-customer-details?sessionId=${sessionId}`);
            return payLoad;
        } catch (err) {
            console.error(`ChatContext:requestCustomerUpdate`, err);
        }
        return null;
    };


    return (
        <SnpApiContext.Provider
            value={{
                keyShopHash,
                registerChatKey,
                customToastFunc,
                setCustomToastFunc,
                keyUserDetails,
                registerUserDetails,
                deRegisterKey,
                createChatSession,
                stopChatSession,
                uploadImage,
                connectWsSession,
                sendPayLoadMessage,
                stopWsSession,
                updateCustomerDetails,
                getShopMessage,
                getLiveMessages,
                updateChatSessionDestinationDetails,
                exportChatSession,
                getCustomeDetailsAvailable,
                requestCustomerUpdate
            }}>
            {children}
        </SnpApiContext.Provider>
    );
};

export const useApiCall = () => useContext(SnpApiContext);
